import React from 'react';
import { Loader2, CheckCircle2, PenLine } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { SignaturePad, SignaturePadHandle } from '../ui/SignaturePad';

// Signature du client sur l'écran du vendeur : volontairement très simple. Le client lit trois phrases, coche une case,
// signe au doigt. Tout le reste (marque « signé à domicile », sceau du PDF, copie par e-mail avec le formulaire de
// rétractation, délais de paiement et de rendez-vous) est fait par le serveur : functions/api/modules/demarchage.js.
// Points juridiques à faire valider : docs/commerciaux/04-POINTS-A-FAIRE-VALIDER.md.

interface Info {
    possible: boolean;
    raison: string;
    email: string;
    nom_par_defaut: string;
    devis_number: string;
    montant: string;
    mention: string;
    retractation_days: number;
    dates: { signe_le: string; paiement_possible_le: string; fin_retractation: string; intervention_possible_le: string };
    formulaire_retractation: string[];
}

const jour = (j: string) => new Date(`${j}T12:00:00`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });

export function SignatureSurPlaceModal({ dossierId, open, onClose, onDone }: { dossierId: string; open: boolean; onClose: () => void; onDone: () => void }) {
    const padRef = React.useRef<SignaturePadHandle>(null);
    const [info, setInfo] = React.useState<Info | null>(null);
    const [chargement, setChargement] = React.useState(true);
    const [nom, setNom] = React.useState('');
    const [accepte, setAccepte] = React.useState(false);
    const [envoi, setEnvoi] = React.useState(false);
    const [erreur, setErreur] = React.useState('');
    const [resultat, setResultat] = React.useState<{ copie_envoyee: boolean; email: string; regle: any } | null>(null);

    React.useEffect(() => {
        if (!open) return;
        let vivant = true;
        setChargement(true); setErreur(''); setResultat(null); setAccepte(false);
        moduleApi.getPrestationSignatureSurPlaceInfo(dossierId)
            .then((r: any) => {
                if (!vivant) return;
                if (r?.error) { setErreur(r.error); return; }
                setInfo(r); setNom(r.nom_par_defaut || '');
            })
            .catch((e: any) => { if (vivant) setErreur(e?.message || 'Chargement impossible.'); })
            .finally(() => { if (vivant) setChargement(false); });
        return () => { vivant = false; };
    }, [open, dossierId]);

    const signer = async () => {
        if (!info) return;
        const signature = padRef.current?.toDataUrl();
        if (!signature) { setErreur('Le client doit signer avec le doigt dans le cadre.'); return; }
        setEnvoi(true); setErreur('');
        try {
            // Le client a lu les informations affichées au-dessus de la case : cocher la case vaut acceptation et prise de connaissance.
            const r: any = await moduleApi.signPrestationSurPlace(dossierId, { signer_name: nom.trim(), signature, mention_acceptee: accepte, informations_recues: accepte });
            if (r?.error) { setErreur(r.error); return; }
            setResultat({ copie_envoyee: !!r.copie_envoyee, email: r.email, regle: r.regle });
        } catch (e: any) { setErreur(e?.message || 'La signature a échoué.'); }
        finally { setEnvoi(false); }
    };

    const fermer = () => { if (resultat) onDone(); onClose(); };
    const pret = !!info?.possible && accepte && nom.trim().length >= 2;

    return (
        <Modal open={open} onClose={fermer} size="md" title="Signature du client"
            subtitle={info ? `${info.devis_number ? `Devis n° ${info.devis_number} · ` : ''}${info.montant}` : undefined}
            icon={<PenLine size={18} />}
            footer={resultat ? (
                <Button onClick={fermer}>Fixer le premier rendez-vous</Button>
            ) : (
                <>
                    <Button variant="secondary" onClick={fermer} disabled={envoi}>Annuler</Button>
                    <Button onClick={signer} disabled={!pret || envoi}>
                        {envoi ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Signer
                    </Button>
                </>
            )}>
            {chargement ? (
                <div className="flex justify-center py-10"><Loader2 className="animate-spin text-content-muted" size={22} /></div>
            ) : !info ? (
                <p className="text-sm text-red-500 font-semibold">{erreur || 'Chargement impossible.'}</p>
            ) : resultat ? (
                <div className="space-y-3">
                    <div className="flex items-start gap-2.5 p-3 rounded-xl bg-green-500/10 border border-green-500/30">
                        <CheckCircle2 size={18} className="text-green-500 shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-bold text-content">Devis signé.</p>
                            <p className="text-xs text-content-muted mt-0.5">
                                {resultat.copie_envoyee ? `Le client reçoit sa copie par e-mail (${resultat.email}).` : `La copie n'a pas pu être envoyée à ${resultat.email} : elle est dans les documents du dossier.`}
                            </p>
                        </div>
                    </div>
                    {resultat.regle?.intervention_possible_le && (
                        <p className="text-sm text-content">Premier rendez-vous possible à partir du <span className="font-bold">{jour(resultat.regle.intervention_possible_le)}</span>.</p>
                    )}
                </div>
            ) : !info.possible ? (
                <p className="text-sm text-content font-semibold">{info.raison}</p>
            ) : (
                <div className="space-y-4">
                    <ul className="text-sm text-content space-y-1">
                        <li>Vous pouvez vous rétracter jusqu'au <span className="font-bold">{jour(info.dates.fin_retractation)}</span>.</li>
                        <li>Rien à payer avant le <span className="font-bold">{jour(info.dates.paiement_possible_le)}</span>.</li>
                        <li>Premier rendez-vous à partir du <span className="font-bold">{jour(info.dates.intervention_possible_le)}</span>.</li>
                    </ul>
                    <details className="text-xs text-content-muted">
                        <summary className="cursor-pointer">Formulaire de rétractation</summary>
                        <div className="mt-1.5 space-y-0.5">{info.formulaire_retractation.map((l, i) => <p key={i}>{l}</p>)}</div>
                    </details>

                    <label className="block">
                        <span className="block text-[11px] font-bold uppercase tracking-[.09em] text-content-muted mb-1.5">Nom du client</span>
                        <input value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Prénom et nom"
                            className="w-full bg-surface-soft border border-border rounded-lg px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                    </label>

                    <label className="flex items-start gap-2.5 text-sm text-content cursor-pointer">
                        <input type="checkbox" checked={accepte} onChange={(e) => setAccepte(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-brand-600" />
                        <span>{info.mention}</span>
                    </label>

                    <SignaturePad ref={padRef} height={170} />
                    <p className="text-[11px] text-content-muted">Le client reçoit sa copie signée par e-mail ({info.email || 'adresse manquante'}).</p>
                    {erreur && <p className="text-xs text-red-500 font-semibold">{erreur}</p>}
                </div>
            )}
        </Modal>
    );
}
